import {
  type GetPtoSummaryArguments,
  type GetPtoSummaryResponseVM,
} from '@/app/services/solution/types/get-pto-summary';
import { type PtoPerDayDeviceKey, type PtoPerCycleDeviceKey } from '@/app/services/solution/types/get-pto-devices';

const PER_DAY_PTO_PER_KM: Record<PtoPerDayDeviceKey, number> = {
  TempControlled: 0.04,
  RefuseCollectorSingle: 0.08,
  RefuseCollectorDouble: 0.1,
  CraneAvg: 0.05,
  CraneHigh: 0.07,
};

const PER_CYCLE_PTO_KWH: Record<PtoPerCycleDeviceKey, number> = {
  Hooklift: 2.5,
  Skiploader: 2,
  TailLift: 1,
  Tipper: 1.8,
};

const DEFAULT_DAILY_DISTANCE_KM = 400;
const DEFAULT_DRIVING_DURATION_SECONDS = 8 * 60 * 60;

const roundToTwo = (value: number): number => Number(value.toFixed(2));

const getPtoSummary = (ptoSummaryArguments: GetPtoSummaryArguments): GetPtoSummaryResponseVM => {
  const drivingDistanceKm = DEFAULT_DAILY_DISTANCE_KM;
  const drivingDurationS = DEFAULT_DRIVING_DURATION_SECONDS;

  const perDayPtoAmountKwh = ptoSummaryArguments.ptoDevicePerDayKey
    ? PER_DAY_PTO_PER_KM[ptoSummaryArguments.ptoDevicePerDayKey]
    : 0;

  const perDayPtoAmountDurationS = ptoSummaryArguments.ptoDevicePerDayKey ? drivingDurationS : 0;

  const perDayTotalPerKm = perDayPtoAmountKwh;
  const perDayTotalDaily = perDayTotalPerKm * drivingDistanceKm;

  const perCyclePtoAmountKwh = ptoSummaryArguments.ptoDevicePerCycleKey
    ? PER_CYCLE_PTO_KWH[ptoSummaryArguments.ptoDevicePerCycleKey]
    : 0;

  const perCyclePtoAmountCount = ptoSummaryArguments.ptoDevicePerCycleCount ?? 0;

  const perCycleTotalDaily = perCyclePtoAmountKwh * perCyclePtoAmountCount;
  const perCycleTotalPerKm = perCycleTotalDaily / drivingDistanceKm;

  const ptoDeviceCustomKwh = ptoSummaryArguments.ptoDeviceCustomKWh ?? 0;
  const customPtoPerKm = ptoDeviceCustomKwh / drivingDistanceKm;

  const totalPtoPerKm = roundToTwo(perDayTotalPerKm + perCycleTotalPerKm + customPtoPerKm);
  const totalPtoDaily = roundToTwo(perDayTotalDaily + perCycleTotalDaily + ptoDeviceCustomKwh);

  return {
    ptoDevicePerDayAmountKWh: roundToTwo(perDayPtoAmountKwh),
    ptoDevicePerDayDurationS: perDayPtoAmountDurationS,
    ptoDevicePerCycleAmountKWh: roundToTwo(perCyclePtoAmountKwh),
    ptoDevicePerCycleCount: perCyclePtoAmountCount,
    ptoDeviceCustomKWh: roundToTwo(ptoDeviceCustomKwh),
    totalPtoPerKm,
    totalPtoDaily,
  };
};

export default getPtoSummary;
